import {
  Box,
  FormControl,
  Stack,
  TextField,
  IconButton,
  Button,
  useTheme,
  Typography,
  Switch,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import { KeyboardEvent, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import ClearIcon from '@mui/icons-material/Clear';
import useOnlineMedia from 'src/services/useOnlineMedia';
import { fillParamAdvanceSearchEmpty } from 'src/utils/fillParamAdvanceSearchEmpth';
import TooltipCustomize from 'src/components/TooltipCustomize';
import RefreshIcon from '@mui/icons-material/Refresh';
import DateRangeLabel from 'src/layouts/SidebarLayout/components/DateRangeLabel';
import SelectDateRangeRetail from 'src/components/Retail/SelectDateRangeRetail';
import Information from 'src/components/Information';
import { reducerUpdateIncludeClipping } from 'src/features/onlineMediaStream';

const CustomBox = styled(Box)(
  ({ theme }) => `
    // width: calc(100vw - ${theme.sidebar.width} - ${theme.spacing(10)});
    margin: ${theme.spacing(2, 0)};
    // position: absolute;
`
);

const FilterBox = styled(Box)(
  ({ theme }) => `
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`
);

function BottomHeader() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const [localSearch, setLocalSearch] = useState<string>('');
  const advanceSearchOnlineMedia = useAppSelector(
    (state) => state.storeAdvanceSearchOnlineMedia
  );
  const projectId = useAppSelector((state) => state.users.project.id);
  const { dateRangeFormatted } = useAppSelector(
    (state) => state.analyticArguments
  );

  const { statisticGranularity, includeClipping } = useAppSelector(
    (state) => state.storeOnlineMediaStream
  );

  const fillAdvanceSearchOnlineMedia = useMemo(() => {
    return fillParamAdvanceSearchEmpty(advanceSearchOnlineMedia);
  }, [advanceSearchOnlineMedia]);

  const { getAllMetricStatistic } = useOnlineMedia();
  const timezone = useAppSelector((state) => state.users.project.timezone);

  const finalPayload = {
    ...fillAdvanceSearchOnlineMedia,
    projectId,
    ...dateRangeFormatted,
    granularity: statisticGranularity,
    timezone,
    page: 1,
    limit: 20
  };

  const handleSearch = () => {
    getAllMetricStatistic({ ...finalPayload, phrases: [localSearch] });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') handleSearch();
  };

  const handleClear = () => {
    setLocalSearch('');
    getAllMetricStatistic({ ...finalPayload, phrases: [] });
  };

  const handleRefresh = () => {
    getAllMetricStatistic(finalPayload);
  };

  const handleSwitchToggleStreamOrClipping = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(reducerUpdateIncludeClipping(event.target.checked));
  };

  return (
    <CustomBox>
      <FilterBox flexWrap={'wrap'} gap={2}>
        <Stack
          direction="row"
          alignItems="center"
          width={matchesUpMd ? 'unset' : '100%'}
          spacing={2}
        >
          <TextField
            size="small"
            sx={{ width: { xs: '100%', md: 400 } }}
            InputProps={{
              startAdornment: (
                <IconButton size="small" onClick={handleSearch}>
                  <SearchTwoToneIcon htmlColor="#757575" />
                </IconButton>
              ),
              sx: { borderRadius: '16px', backgroundColor: 'white' },
              endAdornment: localSearch ? (
                <IconButton size="small" onClick={handleClear}>
                  <ClearIcon />
                </IconButton>
              ) : null
            }}
            placeholder="Search media"
            onChange={(e) => setLocalSearch(e.target.value)}
            value={localSearch}
            onKeyDown={handleKeyDown}
          />
          <FormControl
            size="small"
            sx={{ display: !matchesUpMd && 'none', width: 135 }}
          >
            <SelectDateRangeRetail
              selectProps={{
                sx: {
                  borderRadius: '16px',
                  width: '135px'
                }
              }}
            />
          </FormControl>
        </Stack>
        <Stack
          direction={'row'}
          width={matchesUpMd ? 'unset' : '100%'}
          spacing={1}
        >
          <FormControl
            size="small"
            sx={{
              display: matchesUpMd && 'none',
              width: matchesUpMd ? 135 : '100%'
            }}
          >
            <SelectDateRangeRetail
              selectProps={{
                sx: {
                  borderRadius: '16px',
                  width: matchesUpMd ? '135px' : '100%'
                }
              }}
            />
          </FormControl>
          <TooltipCustomize title="Refresh all metric" placement="top-start">
            <Button
              variant="contained"
              onClick={handleRefresh}
              sx={{
                borderRadius: '6px',
                height: theme.spacing(5),
                minWidth: theme.spacing(6),
                background: theme.palette.primary.main
              }}
            >
              <RefreshIcon />
            </Button>
          </TooltipCustomize>
        </Stack>
      </FilterBox>
      <Stack spacing={1} direction="row" alignItems="center">
        <DateRangeLabel label={`Viewing data within`} />
        {matchesUpMd && (
          <Stack
            direction="row"
            spacing={1}
            width={'100%'}
            alignItems="center"
            sx={{ mt: '16px !important', width: '25%' }}
          >
            <Typography fontSize="13px">All Streams</Typography>
            <Switch
              name="streamOrClipping"
              checked={includeClipping}
              onChange={handleSwitchToggleStreamOrClipping}
            />
            <Typography fontSize="13px">All Clippings Only</Typography>
            <Information content="Showing data from all clipping only." />
          </Stack>
        )}
      </Stack>
      {!matchesUpMd && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mt: '16px !important', width: matchesUpMd ? '25%' : '100%' }}
        >
          <Typography fontSize="13px">All Streams</Typography>
          <Switch
            name="streamOrClipping"
            checked={includeClipping}
            onChange={handleSwitchToggleStreamOrClipping}
          />
          <Typography fontSize="13px">All Clippings Only</Typography>
          <Information content="Showing data from all clipping only." />
        </Stack>
      )}
    </CustomBox>
  );
}

export default BottomHeader;
